import styled, { css } from 'styled-components'

import { Alert } from '@/components/ui/alert'

export const Body = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['24'],
}))

export const Section = styled.section(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['8'],
}))

export const SectionHeader = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['12'],
  justifyContent: 'space-between',
}))

export const SampleSwitcher = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['4'],
  minWidth: 0,
}))

// Note: highlights the configured column (its 1-based position in the preview table) and makes
// the preview inert - it's a picture of the table, not a second working copy of it.
export const Preview = styled.div<{ $columnPosition: number }>(
  ({ theme, $columnPosition }) => css`
    // Note: hovering stays on, so the person cell's full-details tooltip works here too, but its
    // links and buttons are inert - the preview row isn't a real one to open.
    a,
    button {
      pointer-events: none;
    }

    th:nth-child(${$columnPosition}),
    td:nth-child(${$columnPosition}) {
      background-color: ${theme.colors.blue100};
    }
  `,
)

export const FieldList = styled.ul(({ theme }) => ({
  '& > li + li': {
    borderTop: `${theme.border.size['1']} solid ${theme.colors.gray400}`,
  },
  border: `${theme.border.size['1']} solid ${theme.colors.gray400}`,
  borderRadius: theme.border.radius['6'],
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  margin: 0,
  padding: 0,
}))

export const FieldRow = styled.li(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['16'],
  justifyContent: 'space-between',
  padding: `${theme.space['8']} ${theme.space['16']}`,
}))

export const FieldText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
})

export const FieldDescription = styled.span<{ $muted?: boolean }>(({ theme, $muted }) => ({
  color: theme.colors.black400,
  fontSize: '1.2rem',
  fontStyle: $muted ? 'italic' : undefined,
  lineHeight: '1.6rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const AlwaysOn = styled.span(({ theme }) => ({
  alignItems: 'center',
  color: theme.colors.black400,
  display: 'inline-flex',
  gap: theme.space['4'],
}))

export const FooterStart = styled.div({
  marginRight: 'auto',
})

export const Loader = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.space['24'],
}))

// Note: the design's compact "Information notice" (Figma "Column data settings"): full content
// width, 8px / 16px padding, a 20px icon centered on the text block, and Medium 14/20 text. Kept
// local - the shared Alert stays as is for the rest of the app.
export const InfoBanner = styled(Alert).attrs({ iconSize: '2rem' })(({ theme }) => ({
  '& p': {
    fontWeight: 500,
  },
  alignItems: 'center',
  alignSelf: 'stretch',
  padding: `${theme.space['8']} ${theme.space['16']}`,
}))
